import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Form,
  Label,
  Input,
  Button,
  Modal,
  ModalBody,
  Alert,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import Loader from "../../../components/Common/Loader";
import { translateApiError } from "../../../helpers/translateApiError";

const Every8DConnectionModal = ({ show, onClose, onConnect }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      account: "",
      password: "",
    },
    validationSchema: Yup.object({
      account: Yup.string().required(
        t("integration.every8dModal.accountRequired")
      ),
      password: Yup.string().required(
        t("integration.every8dModal.passwordRequired")
      ),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        await onConnect(values);
        // Show success state
        setShowSuccess(true);
      } catch (error) {
        console.error("Failed to connect to Every8D:", error);

        // Extract error message from various possible error structures
        let errorMessage = null;

        if (
          error?.response?.data?.errors &&
          Array.isArray(error.response.data.errors)
        ) {
          errorMessage = error.response.data.errors.join(", ");
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        setSubmitError(
          errorMessage
            ? translateApiError({ code: error?.code, message: errorMessage })
            : t("integration.every8dModal.connectFailed")
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    validation.resetForm();
    setShowPassword(false);
    setSubmitError(null);
    setShowSuccess(false);
    onClose();
  };

  const handleSuccessClose = () => {
    handleClose();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Modal
      isOpen={show}
      toggle={handleClose}
      centered={true}
      className="every8d-connection-modal"
      style={{ maxWidth: "500px" }}
    >
      <ModalBody
        style={{
          padding: "30px 33px",
          border: "1px solid #f0f4f6",
          borderRadius: "4px",
        }}
      >
        {!showSuccess ? (
          // Form State
          <div className="d-flex flex-column gap-4 align-items-center">
            {/* Header */}
            <div className="text-center w-100">
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#063a5e",
                  lineHeight: "1.3",
                  marginBottom: "10px",
                }}
              >
                {t("integration.every8dModal.title")}
              </h3>
              <p
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#878a99",
                  lineHeight: "1.5",
                  marginBottom: 0,
                }}
              >
                {t("integration.every8dModal.subtitle")}
              </p>
            </div>

            {/* Form */}
            <Form
              className="w-100"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <div className="d-flex flex-column gap-3">
                {/* Error Alert */}
                {submitError && (
                  <Alert color="danger" className="mb-0">
                    {submitError}
                  </Alert>
                )}

                {/* EVERY8D Account Field */}
                <div className="d-flex flex-column" style={{ gap: "4px" }}>
                  <Label
                    htmlFor="account"
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#616161",
                      lineHeight: "1.2",
                      marginBottom: 0,
                    }}
                  >
                    {t("integration.every8dModal.accountLabel")}{" "}
                    <span style={{ color: "#e94a53" }}>*</span>
                  </Label>
                  <Input
                    id="account"
                    name="account"
                    type="text"
                    placeholder=" "
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "16px",
                      height: "40px",
                      borderColor: "#e0e0e0",
                      borderRadius: "4px",
                    }}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.account || ""}
                    invalid={
                      validation.touched.account && validation.errors.account
                    }
                  />
                  {validation.touched.account && validation.errors.account && (
                    <FormFeedback style={{ display: "block" }}>
                      {validation.errors.account}
                    </FormFeedback>
                  )}
                </div>

                {/* EVERY8D Password Field */}
                <div className="d-flex flex-column" style={{ gap: "4px" }}>
                  <Label
                    htmlFor="password"
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#616161",
                      lineHeight: "1.2",
                      marginBottom: 0,
                    }}
                  >
                    {t("integration.every8dModal.passwordLabel")}{" "}
                    <span style={{ color: "#e94a53" }}>*</span>
                  </Label>
                  <div style={{ position: "relative" }}>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder=" "
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "16px",
                        height: "40px",
                        borderColor: "#e0e0e0",
                        borderRadius: "4px",
                        paddingRight: "40px",
                      }}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.password || ""}
                      invalid={
                        validation.touched.password &&
                        validation.errors.password
                      }
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      style={{
                        position: "absolute",
                        right: "16px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                      }}
                    >
                      <i
                        className={
                          showPassword ? "ri-eye-line" : "ri-eye-off-line"
                        }
                        style={{ fontSize: "18px", color: "#616161" }}
                      />
                    </button>
                  </div>
                  {validation.touched.password &&
                    validation.errors.password && (
                      <FormFeedback style={{ display: "block" }}>
                        {validation.errors.password}
                      </FormFeedback>
                    )}
                </div>

                {/* Buttons */}
                <div className="d-flex flex-column" style={{ gap: "8px" }}>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    color="primary"
                    className="btn-send-invitation"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader color="light" size="sm" />
                        <span className="ms-2">{t("common.saving")}</span>
                      </>
                    ) : (
                      t("common.actions.save")
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="btn-cancel"
                  >
                    {t("common.actions.cancel")}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
        ) : (
          // Success State
          <div className="d-flex flex-column gap-4 align-items-center">
            {/* Success Icon */}
            <div
              style={{
                width: "96px",
                height: "96px",
                borderRadius: "50%",
                backgroundColor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "#063a5e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="ri-check-line"
                  style={{ fontSize: "40px", color: "#ffffff" }}
                />
              </div>
            </div>

            {/* Success Message */}
            <div className="text-center w-100">
              <h3
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "20px",
                  fontWeight: 600,
                  color: "#063a5e",
                  lineHeight: "1.3",
                  marginBottom: "10px",
                }}
              >
                {t("integration.every8dModal.successTitle")}
              </h3>
              <p
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#757575",
                  lineHeight: "1.5",
                  marginBottom: 0,
                }}
              >
                {t("integration.every8dModal.successMessage")}
              </p>
            </div>

            {/* Close Button */}
            <Button
              onClick={handleSuccessClose}
              type="button"
              color="primary"
              className="btn-send-invitation"
            >
              {t("common.actions.close")}
            </Button>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

Every8DConnectionModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConnect: PropTypes.func.isRequired,
};

export default Every8DConnectionModal;
